import { type ReactNode } from 'react';

/**
 * The modes, in a fixed order.
 *
 * Every name is short and concrete, and every one carries an (i) button
 * opening Quick Reference at that entry — teachers repeatedly reported not
 * knowing what a mode was for, so the affordance is a requirement rather than
 * a nicety.
 */
export const MODES = [
  'Read',
  'Ask',
  'Claims',
  'Protect',
  'Ledgers',
  'Compare',
  'Room',
  'Verify',
  /*
   * Two surfaces over work that already existed and had nowhere to live: the
   * 21 entitlement figures whose grounding is settled, and what changed in the
   * school's next draft. Added at the end because the order of this list is the
   * order of the nav, and it runs roughly in the order a person needs them.
   */
  'Calculators',
  'Drafts',
  /*
   * What the staff said. Sits beside Drafts rather than at the end, because
   * during a bargaining round it is worked in rather than read occasionally —
   * a claim is only as strong as the evidence the group has that it matters to
   * anybody, and this is where that evidence arrives.
   */
  'Survey',
  /*
   * Where a representative finds out what the model settled on their behalf.
   * Last in the nav because it is read occasionally rather than worked in —
   * the whole point of the surface is that it does not need attending to for
   * the rest of the app to function.
   */
  'Report',
] as const;
export type Mode = (typeof MODES)[number];

/** Nominal expiry of the agreement. The reason the product exists. */
const EXPIRY = new Date('2027-01-31T00:00:00+11:00');

function daysRemaining(from: Date): number {
  return Math.max(0, Math.ceil((EXPIRY.getTime() - from.getTime()) / 86_400_000));
}

export function Shell({
  mode,
  onMode,
  onGuide,
  rail,
  canvas,
  dock,
  who,
  unpublished,
  onAccount,
  onSignOut,
}: {
  mode: Mode;
  onMode: (mode: Mode) => void;
  onGuide: (entry: Mode) => void;
  rail?: ReactNode;
  canvas: ReactNode;
  dock?: ReactNode;
  who: string;
  unpublished: number;
  onAccount: () => void;
  onSignOut: () => void;
}) {
  const days = daysRemaining(new Date());

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <header
        className="flex shrink-0 items-center gap-4 border-b px-4"
        style={{ borderColor: 'var(--color-rule-strong)', height: 44 }}
      >
        {/*
          The app's name, not the instrument's.

          This slot read "Teachers MEA 2023" — the primary instrument's short
          name in the position every other application puts its own name. It
          survived the rename to Teachers Lounge because I searched for the old
          product name and this was never the product name; it was the document
          title standing in for one. Four places carried the brand and this was
          the only one a reader actually sees, so the rename landed everywhere
          except the screen.

          The instrument still identifies itself where it is being read — the
          clause rail and the reader both name it — and it should not be the
          masthead, because three other instruments are in this corpus and a
          representative comparing against the Award is not leaving Teachers
          Lounge to do it.
        */}
        <span className="clause-number text-[11px] font-semibold uppercase tracking-[0.08em]">
          Teachers Lounge
        </span>

        {/*
          Mono pills, and one (i) rather than ten.
          
          Every mode carried its own info button, so the nav was ten names
          alternating with ten identical circles — the circles took as much
          width as the words and made the one thing the bar exists for, telling
          you where you are, the harder of the two to read. One button that
          explains the surface you are on says the same thing in a tenth of the
          space, and stops the count growing every time a mode is added.

          Uppercase mono for the names because they are chrome, not prose: the
          same treatment the clause numbers and micro-labels get, and what
          separates the navigation from the document it sits above.
        */}
        <nav className="flex items-center gap-1.5" aria-label="Modes">
          {MODES.map((item) => {
            const active = item === mode;
            return (
              <button
                key={item}
                onClick={() => onMode(item)}
                aria-current={active ? 'page' : undefined}
                /*
                  `.pill` rather than the six inline declarations this used to
                  carry. The same control had been written out longhand here,
                  on the Ledgers table tabs and on the objection ratings, and
                  three surfaces spelling one control is where a radius starts
                  disagreeing with itself.
                */
                className={`hit pill clause-number text-[10.5px] uppercase tracking-[0.08em] ${
                  active ? 'pill-active' : ''
                }`}
              >
                {item}
              </button>
            );
          })}
          <button
            className="info-button ml-0.5"
            aria-label={`What is ${mode} for?`}
            onClick={() => onGuide(mode)}
          >
            i
          </button>
        </nav>

        <div className="ml-auto flex items-center gap-4">
          {/* Quiet, small, always there. */}
          <span className="label" title="Nominal expiry, 31 January 2027">
            {days} days
          </span>

          {/* Private-by-default only works if forgetting to publish is
              visible. Your own count, never the group's — a count across
              seven people is close enough to an attribution. */}
          {unpublished > 0 ? (
            <span className="label" title="Marks only you can see">
              ◌ {unpublished} unpublished
            </span>
          ) : null}

          {/* Who the marks you are about to make will belong to. Worth being
              certain of on a shared staffroom machine, so it is a permanent
              fixture rather than something behind a menu. */}
          <button onClick={onAccount} className="hit label underline" title="Your account">
            {who}
          </button>
          <button onClick={onSignOut} className="hit label underline">
            Sign out
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {rail ? (
          <aside
            className="min-h-0 shrink-0 overflow-y-auto border-r"
            style={{ width: 'var(--rail-width)', borderColor: 'var(--color-rule)' }}
          >
            {rail}
          </aside>
        ) : null}

        <main className="min-h-0 flex-1 overflow-y-auto">{canvas}</main>

        {/* The dock is hidden below 1024px rather than made responsive: the
            claim workbench is not worth compromising desktop density for. */}
        {dock ? (
          <aside
            className="hidden min-h-0 shrink-0 overflow-y-auto border-l lg:block"
            style={{ width: 'var(--dock-width)', borderColor: 'var(--color-rule)' }}
          >
            {dock}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
